import { FC, useRef, useState } from "react";

import { Calendar } from "shared/ui";

import type { DateRange } from "../../model";
import { formatDisplayDate, parseISODate, toISODate } from "../../lib";
import { useDateRangeSelection } from "./useDateRangeSelection";
import { useOutsideClick } from "./useOutsideClick";
import styles from "./DateRangePicker.module.css";

interface DateRangePickerProps {
  value: DateRange | null;
  onApply: (range: DateRange) => void;
  onReset: () => void;
}

export const DateRangePicker: FC<DateRangePickerProps> = ({
  value,
  onApply,
  onReset,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const {
    viewDate,
    rangeStart,
    rangeEnd,
    selectDay,
    goToPrevMonth,
    goToNextMonth,
    reset,
  } = useDateRangeSelection();

  useOutsideClick(ref, () => setOpen(false));

  const handleApply = () => {
    if (!rangeStart) return;
    onApply({
      dateFrom: toISODate(rangeStart),
      dateTo: toISODate(rangeEnd ?? rangeStart),
    });
    setOpen(false);
  };

  const handleReset = () => {
    reset();
    onReset();
    setOpen(false);
  };

  const label = value
    ? `${formatDisplayDate(parseISODate(value.dateFrom))} – ${formatDisplayDate(parseISODate(value.dateTo))}`
    : "Last 30 days";

  return (
    <div className={styles.wrapper} ref={ref}>
      <button
        type="button"
        className={styles.trigger}
        onClick={() => setOpen((v) => !v)}
      >
        {label}
      </button>

      {open && (
        <div className={styles.popup}>
          <Calendar
            viewDate={viewDate}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            onPrevMonth={goToPrevMonth}
            onNextMonth={goToNextMonth}
            onSelectDay={selectDay}
          />

          <div className={styles.actions}>
            <button
              type="button"
              className={styles.resetBtn}
              onClick={handleReset}
            >
              Reset
            </button>
            <button
              type="button"
              className={styles.applyBtn}
              disabled={!rangeStart}
              onClick={handleApply}
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
